import type { WorldManifest } from "@townsy/contracts";

/**
 * World v5 (ADR-0041): which district a street item belongs to, and the one-district view of the manifest. A leaf
 * module (contracts types only), so traffic.ts and vehicles.ts can use it without an import cycle (manifest -> venues
 * -> vehicles); districtSlice.ts builds the full district slice (streets, traffic loops) on top of it.
 *
 * World v6 (ADR-0042, one continuous town): the street-geometry functions (streetColliders, streetDecor, streetSeats,
 * drivableRects, trafficLoops) work on EVERY item of the manifest they are handed: the whole manifest means the whole
 * town (one street room), a district slice means that district. The v5 rule "the whole manifest means its first
 * district" (`streetDistrictId` / `streetSlice`) is gone.
 */

type Tagged = { districtId?: string | undefined };

/**
 * The district an item belongs to: its own `districtId` or, for an untagged item (every item from before v5, and the v4
 * items v6 changed in place), the manifest's first district (Central).
 */
export function itemDistrictId(m: Pick<WorldManifest, "districts">, item: Tagged): string {
  return item.districtId ?? m.districts[0]!.id;
}

const ITEMS = new WeakMap<WorldManifest, Map<string, WorldManifest>>();

/**
 * `m` restricted to one district: that district, its plots, billboards, surfaces, props and colliders (manifest
 * order kept), with the shared interiors and movement settings. Cached per manifest object; a one-district manifest
 * of that district is returned as it is.
 */
export function districtItems(m: WorldManifest, districtId: string): WorldManifest {
  if (m.districts.length === 1 && m.districts[0]!.id === districtId) return m;
  let byId = ITEMS.get(m);
  if (!byId) {
    byId = new Map();
    ITEMS.set(m, byId);
  }
  const cached = byId.get(districtId);
  if (cached) return cached;
  const district = m.districts.find((d) => d.id === districtId);
  if (!district) throw new Error(`unknown district ${districtId}`);
  const own = <T extends Tagged>(items: readonly T[]): T[] => items.filter((item) => itemDistrictId(m, item) === districtId);
  const slice: WorldManifest = {
    version: m.version,
    worldId: m.worldId,
    districts: [district],
    plots: m.plots.filter((p) => p.districtId === districtId),
    billboards: m.billboards.filter((b) => b.districtId === districtId),
    surfaces: own(m.surfaces),
    props: own(m.props),
    colliders: own(m.colliders),
    interiors: m.interiors,
    movement: m.movement,
  };
  byId.set(districtId, slice);
  return slice;
}
